import React, { useCallback, useMemo } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { Box, Text } from '@stitch-fix/mode-react';
import { useProductDetailsPageContext } from '../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';
import SizeGuideLinkAndModal from '../SizeGuide/SizeGuideLinkAndModal';
import SizeLegendLabel from './SizeLegendLabel';
import RecommendedSizeLegendLabel from './RecommendedSizeLegendLabel';
import AllSizesButtonGroup from './AllSizesButtonGroup';
import RecommendedSizesButtonGroup from './RecommendedSizesButtonGroup';
import RecommendedInseamsButtonGroup from './RecommendedInseamsButtonGroup';
import AllInseamsButtonGroup from './AllInseamsButtonGroup';
import AllSizesTracker from '../Gated/src/tracking/allSizesTracker';
import SourceType from '../Gated/src/helpers/SourceType';
import styles from './styles.module.scss';

import { loadTranslations } from '../../../shared/i18n';
import translations from './translations.json';

const { useTranslation } = loadTranslations(translations, 'ProductSizes');

const RECOMMENDED_SIZES_TAB_INDEX = 0;
const ALL_SIZES_TAB_INDEX = 1;

const ProductSizesForPersonalizedShoppingExperiences = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useProductDetailsPageContext();

  const selectedSizeMatchesRecommendedSize = useMemo(
    () => state.selectedSizeLabel === state.recommendedSkuSizeLabel,
    [state.selectedSizeLabel, state.recommendedSkuSizeLabel],
  );

  const selectedInseamMatchesRecommendedInseam = useMemo(
    () => state.selectedInseamLabel === state.recommendedSkuInseamLabel,
    [state.selectedInseamLabel, state.recommendedSkuInseamLabel],
  );

  const fireTabChangeEvent = useCallback(
    (selectedIndex, previouslySelectedIndex) => {
      if (selectedIndex !== previouslySelectedIndex) {
        if (selectedIndex === RECOMMENDED_SIZES_TAB_INDEX) {
          AllSizesTracker.trackRecommendedSizesTabClickEvent({
            collection: state.collectionName || '',
            outfitId: state.outfitId || '',
            productSkuId: state.selectedSkuId || state.fallbackSkuId,
            source: state.source || '',
          });

          dispatch({ type: 'RECOMMENDED_SIZES_TAB_CLICKED' });
        } else if (selectedIndex === ALL_SIZES_TAB_INDEX) {
          AllSizesTracker.trackAllSizesTabClickEvent({
            collection: state.collectionName || '',
            outfitId: state.outfitId || '',
            productSkuId: state.selectedSkuId || state.fallbackSkuId,
            source: state.source || '',
          });

          dispatch({ type: 'ALL_SIZES_TAB_CLICKED' });
        }
      }
    },
    [state, dispatch],
  );

  // HACK (SHOC-1650): There is a known bug where the initial forward from the
  // Ungated PDP to the Gated PDP sets the recommended size to be
  // whatever was selected on the Ungated PDP. This is misleading
  // to users, so until there's a broader backend fix for this issue,
  // we just want to mitigate it by hiding recommended sizes. This issue does
  // not persist upon further link clicking, so we can show sizes after that.
  //
  // Hackily, we can use the `source` query param to detect that we initially came from the Ungated PDP,
  // and then we check for the absence of the `sub_source` query param to ensure this is the first stop.
  const showOnlyAllSizes = useMemo(
    () => state.source === SourceType.UNGATED && !state.subSource,
    [state.source, state.subSource],
  );

  // If the recommended size/inseam combo matches the selected size/inseam combo on page load, the recommended sizes tab should be shown.
  // If they do not match, the all sizes tab should be shown. The use case for this is when selected state URL's are pasted in with
  // size and inseam params included. It wouldn't make sense to show the recommended sizes tab as the default on page load when those size/inseam options
  // aren't the ones that are currently selected.
  const showRecommendedSizesTabsOnLoad = useMemo(
    () =>
      selectedSizeMatchesRecommendedSize &&
      selectedInseamMatchesRecommendedInseam,
    [
      selectedSizeMatchesRecommendedSize,
      selectedInseamMatchesRecommendedInseam,
    ],
  );

  // renders the sizes and renders the inseams
  return (
    <Tabs
      defaultIndex={showRecommendedSizesTabsOnLoad ? 0 : 1}
      onSelect={fireTabChangeEvent}
    >
      {!showOnlyAllSizes && (
        <>
          <Box
            display="flex"
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <TabList className={styles['size-tabs-list']}>
              <Tab
                className={styles['size-tab-header']}
                selectedClassName={styles['size-tab-header-selected']}
              >
                {t('tabs.recommended')}
              </Tab>
              <Tab
                className={styles['size-tab-header']}
                selectedClassName={styles['size-tab-header-selected']}
              >
                {t('tabs.allSizes')}
              </Tab>
            </TabList>
            <Box mb={0.75}>
              <SizeGuideLinkAndModal />
            </Box>
          </Box>

          <TabPanel
            className={styles['size-tab-panel']}
            selectedClassName={styles['size-tab-panel-selected']}
          >
            <RecommendedSizeLegendLabel />
            {/* if we have both a recommended size and an inseam, they should */}
            {/* render side by side */}
            {state.recommendedSizes?.length > 0 &&
            state.recommendedSizes?.filter(
              size => size?.shopProductBySkuId?.variantBySkuId?.inseam?.name,
            ).length > 0 ? (
              <Box styleName="size-inseam-title-container">
                <Box display="flex">
                  <Box mr={0.75}>
                    <Text height="short" family="regular" scale="3">
                      Size
                    </Text>
                    <Box>
                      <RecommendedSizesButtonGroup />
                    </Box>
                  </Box>
                  <Box>
                    <Text height="short" family="regular" scale="3">
                      Inseam
                    </Text>
                    <Box>
                      <RecommendedInseamsButtonGroup />
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box styleName="size-title-container">
                <Box>
                  <Text height="short" family="regular" scale="3">
                    Size
                  </Text>
                  <Box>
                    <RecommendedSizesButtonGroup />
                  </Box>
                </Box>
              </Box>
            )}
          </TabPanel>
        </>
      )}

      <TabPanel
        className={styles['size-tab-panel']}
        selectedClassName={styles['size-tab-panel-selected']}
      >
        <SizeLegendLabel />
        <>
          <Text height="short" family="regular" scale="3">
            Size
          </Text>
          <AllSizesButtonGroup />
        </>
        {state.inseamOptions.filter(inseam => inseam).length > 0 && (
          <>
            <Text height="short" family="regular" scale="3">
              Inseam
            </Text>
            <AllInseamsButtonGroup />
          </>
        )}
      </TabPanel>
    </Tabs>
  );
};

export default React.memo(ProductSizesForPersonalizedShoppingExperiences);
